import React, { useState, useRef, forwardRef, useImperativeHandle } from "react";
import MobileTransactionGroup from "./MobileTransactionGroup";
import Notifier from "../../components/Notifier";
import MobileBulkEditBottomSheet from "./MobileBulkEditBottomSheet";
import MobileBulkSelectionBar from "./MobileBulkSelectionBar";
import { groupTransactionsByCategory } from "../desktop/transactionGroupingUtils";
import Api from "../../utils/Api";

const NOTIFICATIONS = {
    ERROR: "error",
    SUCCESS: "success"
};

const MobileTransactionApprovalItems = forwardRef(({ approvalMode, removeTransactionCallback }, ref) => {
    const [transactions, setTransactions] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [notificationType, setNotificationType] = useState(null);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedTransactions, setSelectedTransactions] = useState(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [lookups, setLookups] = useState(null);

    const categoryLookup = useRef(null);
    const tagLookup = useRef(null);

    const toggleGroupCollapse = (groupName) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupName)) {
                newSet.delete(groupName);
            } else {
                newSet.add(groupName);
            }
            return newSet;
        });
    };

    // Check if a group is collapsed (default is collapsed unless explicitly expanded)
    const isGroupCollapsed = (groupName) => {
        return !expandedGroups.has(groupName);
    };

    useImperativeHandle(ref, () => ({
        setLookups(lookupsData) {
            categoryLookup.current = lookupsData.categories;
            tagLookup.current = lookupsData.tags;
            setLookups(lookupsData);
        },
        setItems(items) {
            setTransactions(items);
        },
        removeTransaction(id) {
            setTransactions(prevTransactions => prevTransactions.filter(item => item.id !== id));
            setSelectedTransactions(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
        }
    }));

    const handleTransactionLongPress = (transactionId) => {
        if (!selectionMode) {
            setSelectionMode(true);
            setSelectedTransactions(new Set([transactionId]));
        }
    };

    const handleTransactionSelection = (transactionId, isSelected) => {
        setSelectedTransactions(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(transactionId);
            } else {
                newSet.delete(transactionId);
            }
            
            // Exit selection mode if no transactions are selected
            if (newSet.size === 0) {
                setSelectionMode(false);
            }
            
            return newSet;
        });
    };

    const handleClearSelection = () => {
        setSelectedTransactions(new Set());
        setSelectionMode(false);
    };

    const handleSelectAll = () => {
        setSelectedTransactions(new Set(transactions.map(t => t.id)));
    };

    const handleBulkEdit = () => {
        setShowBulkEditModal(true);
    };

    const handleBulkApprove = async (editData = null) => {
        const selectedIds = Array.from(selectedTransactions);
        
        try {
            const updates = selectedIds.map(id => {
                const transaction = transactions.find(t => t.id === id);
                return {
                    id,
                    type: transaction.type,
                    categoryId: editData?.categoryId || transaction.categoryId,
                    tags: editData?.tags?.length > 0 ? editData.tags : transaction.tags,
                    remarks: transaction.remarks
                };
            });

            const results = await Promise.all(
                updates.map(update => Api.post("/api/approveTransaction", update))
            );

            const successCount = results.filter(r => r.response.success).length;
            
            if (successCount === selectedIds.length) {
                showSaveSuccess();
                // Remove approved transactions
                selectedIds.forEach(id => removeTransactionCallback(id));
                handleClearSelection();
            } else {
                showSaveError();
            }
        } catch (error) {
            showSaveError();
        }
    };

    const handleBulkSave = (editData) => {
        handleBulkApprove(editData);
    };

    const showSaveError = () => {
        setNotificationType(NOTIFICATIONS.ERROR);
    };

    const showSaveSuccess = () => {
        setNotificationType(NOTIFICATIONS.SUCCESS);
    };

    if (!transactions.length) {
        return (
            <div>
                <h4 style={{ textAlign: "center" }}>לא נמצאו תוצאות</h4>
            </div>
        );
    }

    const { paymentAppTransactions, categoryGroups } = groupTransactionsByCategory(
        transactions,
        categoryLookup.current
    );

    return (
        <div style={{ backgroundColor: "#e6e6e6", paddingBottom: selectionMode ? "80px" : undefined }}>
            {/* Payment App Transactions Group */}
            {paymentAppTransactions.length > 0 && (
                <MobileTransactionGroup
                    groupKey="paymentApps"
                    title="עסקאות BIT/PAYBOX"
                    transactions={paymentAppTransactions}
                    totalAmount={paymentAppTransactions.reduce((sum, t) => sum + (t.amount || 0), 0)}
                    isCollapsed={isGroupCollapsed('paymentApps')}
                    onToggleCollapse={toggleGroupCollapse}
                    isSpecial={true}
                    categoryLookup={categoryLookup.current}
                    tagLookup={tagLookup.current}
                    approvalMode={approvalMode}
                    removeTransactionCallback={removeTransactionCallback}
                    showSaveError={showSaveError}
                    showSaveSuccess={showSaveSuccess}
                    selectionMode={selectionMode}
                    selectedTransactions={selectedTransactions}
                    onTransactionLongPress={handleTransactionLongPress}
                    onTransactionSelection={handleTransactionSelection}
                />
            )}

            {/* Category Groups */}
            {categoryGroups.map((group) => (
                <MobileTransactionGroup
                    key={`category-${group.categoryId}`}
                    groupKey={`category-${group.categoryId}`}
                    title={group.categoryName}
                    transactions={group.transactions}
                    totalAmount={group.totalAmount}
                    isCollapsed={isGroupCollapsed(`category-${group.categoryId}`)}
                    onToggleCollapse={toggleGroupCollapse}
                    isSpecial={false}
                    categoryLookup={categoryLookup.current}
                    tagLookup={tagLookup.current}
                    approvalMode={approvalMode}
                    removeTransactionCallback={removeTransactionCallback}
                    showSaveError={showSaveError}
                    showSaveSuccess={showSaveSuccess}
                    selectionMode={selectionMode}
                    selectedTransactions={selectedTransactions}
                    onTransactionLongPress={handleTransactionLongPress}
                    onTransactionSelection={handleTransactionSelection}
                />
            ))}

            {/* Bulk Selection Bar */}
            <MobileBulkSelectionBar
                selectionMode={selectionMode}
                selectedCount={selectedTransactions.size}
                approvalMode={approvalMode}
                hasTransactions={transactions.length > 0}
                onEnterSelectionMode={() => setSelectionMode(true)}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                onBulkEdit={handleBulkEdit}
                onBulkApprove={handleBulkApprove}
            />

            <MobileBulkEditBottomSheet
                show={showBulkEditModal}
                onHide={() => setShowBulkEditModal(false)}
                lookups={lookups}
                onSave={handleBulkSave}
                selectedCount={selectedTransactions.size}
            />

            <Notifier notificationType={notificationType} />
        </div>
    );
});

export default MobileTransactionApprovalItems;